import React, { useEffect, useRef } from 'react';

import ChevronDownIcon from 'icons/chevron-down.svg';
import ChevronUpIcon from 'icons/chevron-up.svg';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import voca from 'voca';

import OutlinedButton from 'components/application/buttons/OutlinedButton';
import CircleQuestion from 'components/application/CircleQuestion';
import OptionChoice from 'components/application/OptionChoice';
import Options from 'components/application/Options';
import Tooltip from 'components/application/Tooltip';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useTrainingRegisterResources } from 'components/contexts/TrainingRegisterResourceManagementContext';
import CheckboxFilter from 'components/filters/CheckboxFilter';
import MultiSelectFilter from 'components/filters/MultiSelectFilter';

import { personDisplayName } from 'components/helpers/users';

const filterOptionTKeys = {
  courseFilter: 'courses',
  courseRequirementFilter: 'courseRequirement',
  divisionFilter: 'divisions',
  lineManagerFilter: 'lineManagers',
  personnelTypeFilter: 'personnelType',
  roleFilter: 'roles',
  teamFilter: 'teams',
};

export function sortLabelsAlphabetically(a, b) {
  return voca.lowerCase(a.label) > voca.lowerCase(b.label) ? 1 : -1;
}

export function sortDivisions(a, b) {
  if (a.record.attributes.primary) {
    return -1;
  } else if (b.record.attributes.primary) {
    return 1;
  } else {
    return sortLabelsAlphabetically(a, b);
  }
}

function PersonnelTypeFilter({ filter, onChange, onRemoveFilter }) {
  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  return (
    <div className='tw-rounded-sm tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white tw-p-5'>
      <CheckboxFilter
        checkboxes={[
          {
            name: 'personnel_type_filter_employees',
            label: i18nTShared('employees'),
            value: filter.employees,
            onChange: (event) =>
              onChange(
                'personnelTypeFilter',
                'employees',
                event.target.checked,
              ),
          },
          {
            name: 'personnel_type_filter_sub_contractors',
            label: i18nTShared('subcontractors'),
            value: filter.subcontractors,
            onChange: (event) =>
              onChange(
                'personnelTypeFilter',
                'subcontractors',
                event.target.checked,
              ),
          },
        ]}
        label={i18nTShared('personnelType')}
        onRemove={() => onRemoveFilter('personnelTypeFilter')}
      />
    </div>
  );
}

function RoleFilter({
  autoFocus,
  filter,
  onChange,
  onDeselect,
  onFocus,
  onRemoveFilter,
  onSelect,
  validationError,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'trainingRegisterReports.components.Filters.RoleFilter',
  });

  const { t: i18nTCommon } = useTranslation('common');

  return (
    <div className='tw-rounded-sm tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white tw-p-5'>
      <MultiSelectFilter
        autoFocus={autoFocus}
        checkboxes={[
          {
            label: i18nT('checkboxes.additionalTraining.label'),
            name: 'role_filter_additional_training',
            onChange: (event) =>
              onChange(
                'roleFilter',
                'additionalTraining',
                event.target.checked,
              ),
            tooltip: i18nT('checkboxes.additionalTraining.tooltip'),
            value: filter.additionalTraining,
          },
        ]}
        fieldError={validationError}
        id='role_filter_selected_ids'
        label={i18nTCommon('shared.roles')}
        name='role_filter_selected_ids'
        onDeselect={(id) => onDeselect('roleFilter', id)}
        onFocus={() => onFocus('roleFilter')}
        onRemove={() => onRemoveFilter('roleFilter')}
        onSelect={(id) => onSelect('roleFilter', id)}
        placeholder={i18nTCommon('roles.searchRoles')}
        resourceLabel={(resource) => resource.attributes.position}
        resourceSort={sortLabelsAlphabetically}
        selectedIds={filter.selectedIds}
        type='role'
      />
    </div>
  );
}

function CourseFilter({
  autoFocus,
  filter,
  onDeselect,
  onFocus,
  onRemoveFilter,
  onSelect,
  validationError,
}) {
  const { t: i18nTCommon } = useTranslation('common');

  return (
    <div className='tw-rounded-sm tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white tw-p-5'>
      <MultiSelectFilter
        autoFocus={autoFocus}
        fieldError={validationError}
        id='course_filter_selected_ids'
        label={i18nTCommon('shared.courses')}
        name='course_filter_selected_ids'
        onDeselect={(id) => onDeselect('courseFilter', id)}
        onFocus={() => onFocus('courseFilter')}
        onRemove={() => onRemoveFilter('courseFilter')}
        onSelect={(id) => onSelect('courseFilter', id)}
        placeholder={i18nTCommon('courses.search')}
        resourceLabel={(resource) => resource.attributes.name}
        resourceSort={sortLabelsAlphabetically}
        selectedIds={filter.selectedIds}
        type='course'
      />
    </div>
  );
}

function CourseRequirementFilter({ filter, onChange, onRemoveFilter }) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix:
      'trainingRegisterReports.components.Filters.CourseRequirementFilter',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  return (
    <div className='tw-rounded-sm tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white tw-p-5'>
      <CheckboxFilter
        checkboxes={[
          {
            label: i18nT('checkboxes.required.label'),
            name: 'course_requirement_filter_required_training',
            onChange: (event) =>
              onChange(
                'courseRequirementFilter',
                'required',
                event.target.checked,
              ),
            value: filter.required,
          },
          {
            label: i18nT('checkboxes.optional.label'),
            name: 'course_requirement_filter_optional_training',
            onChange: (event) =>
              onChange(
                'courseRequirementFilter',
                'optional',
                event.target.checked,
              ),
            value: filter.optional,
          },
        ]}
        label={i18nTShared('courseRequirement')}
        onRemove={() => onRemoveFilter('courseRequirementFilter')}
      />
    </div>
  );
}

function LineManagerFilter({
  autoFocus,
  division,
  filter,
  onDeselect,
  onFocus,
  onRemoveFilter,
  onSelect,
  validationError,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'trainingRegisterReports.components.Filters.LineManagerFilter',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  return (
    <div className='tw-rounded-sm tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white tw-p-5'>
      <MultiSelectFilter
        autoFocus={autoFocus}
        fieldError={validationError}
        id='line_manager_filter_selected_ids'
        label={i18nTShared('lineManagers')}
        message={division.attributes.primary ? null : i18nT('message')}
        name='line_manager_filter_selected_ids'
        onDeselect={(id) => onDeselect('lineManagerFilter', id)}
        onFocus={() => onFocus('lineManagerFilter')}
        onRemove={() => onRemoveFilter('lineManagerFilter')}
        onSelect={(id) => onSelect('lineManagerFilter', id)}
        placeholder={i18nT('placeholder')}
        resourceLabel={(resource) =>
          personDisplayName({ attributes: resource.attributes })
        }
        resourceSort={sortLabelsAlphabetically}
        selectedIds={filter.selectedIds}
        type='line_manager'
      />
    </div>
  );
}

function DivisionFilter({
  autoFocus,
  filter,
  onDeselect,
  onFocus,
  onRemoveFilter,
  onSelect,
  validationError,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'trainingRegisterReports.components.Filters.DivisionFilter',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  return (
    <div className='tw-rounded-sm tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white tw-p-5'>
      <MultiSelectFilter
        autoFocus={autoFocus}
        fieldError={validationError}
        id='division_filter_selected_ids'
        label={i18nTShared('divisions')}
        name='division_filter_selected_ids'
        onDeselect={(id) => onDeselect('divisionFilter', id)}
        onFocus={() => onFocus('divisionFilter')}
        onRemove={() => onRemoveFilter('divisionFilter')}
        onSelect={(id) => onSelect('divisionFilter', id)}
        placeholder={i18nT('placeholder')}
        resourceLabel={(resource) => resource.attributes.name}
        resourceSort={sortDivisions}
        selectedIds={filter.selectedIds}
        type='division'
      />
    </div>
  );
}

function TeamFilter({
  autoFocus,
  filter,
  isDivisionFilterSelected,
  onDeselect,
  onFocus,
  onRemoveFilter,
  onSelect,
  validationError,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'trainingRegisterReports.components.Filters.TeamFilter',
  });

  const { t: i18nTCommon } = useTranslation('common');

  return (
    <div className='tw-rounded-sm tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white tw-p-5'>
      <MultiSelectFilter
        autoFocus={autoFocus}
        fieldError={validationError}
        id='team_filter_selected_ids'
        label={i18nTCommon('shared.teams')}
        message={isDivisionFilterSelected ? i18nT('message') : null}
        name='team_filter_selected_ids'
        onDeselect={(id) => onDeselect('teamFilter', id)}
        onFocus={() => onFocus('teamFilter')}
        onRemove={() => onRemoveFilter('teamFilter')}
        onSelect={(id) => onSelect('teamFilter', id)}
        placeholder={i18nTCommon('teams.searchTeams')}
        resourceLabel={(resource) => resource.attributes.name}
        resourceSort={sortLabelsAlphabetically}
        selectedIds={filter.selectedIds}
        type='team'
      />
    </div>
  );
}

export default function Filters({
  defaultFilters,
  filters,
  onAddFilter,
  onChange,
  onDeselect,
  onFocus,
  onRemoveFilter,
  onSelect,
  validationErrors = {},
}) {
  const { division: currentDivision } = useCurrentActor();
  const isInitialMount = useRef(true);

  const autoFocus = !isInitialMount.current;

  const {
    hasLineManagerAccess,
    hasTRPersonnelAccess,
    hasTeamManagerAccess,
    hasTeamViewableAccess,
  } = useTrainingRegisterResources();

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const hasLineManagerOnlyAccess =
    hasLineManagerAccess && !(hasTRPersonnelAccess || hasTeamManagerAccess);
  const availableFilters = [...defaultFilters];
  const filterComponents = {
    personnelTypeFilter: (
      <PersonnelTypeFilter
        filter={filters.personnelTypeFilter}
        onChange={onChange}
        onRemoveFilter={onRemoveFilter}
      />
    ),
    roleFilter: (
      <RoleFilter
        autoFocus={autoFocus}
        filter={filters.roleFilter}
        onChange={onChange}
        onDeselect={onDeselect}
        onFocus={onFocus}
        onRemoveFilter={onRemoveFilter}
        onSelect={onSelect}
        validationError={validationErrors.roleFilter}
      />
    ),
    courseFilter: (
      <CourseFilter
        autoFocus={autoFocus}
        filter={filters.courseFilter}
        onDeselect={onDeselect}
        onFocus={onFocus}
        onRemoveFilter={onRemoveFilter}
        onSelect={onSelect}
        validationError={validationErrors.courseFilter}
      />
    ),
    courseRequirementFilter: (
      <CourseRequirementFilter
        filter={filters.courseRequirementFilter}
        onChange={onChange}
        onRemoveFilter={onRemoveFilter}
      />
    ),
  };

  const selectedSet = new Set(filters.selectedFilters);

  if (!hasLineManagerOnlyAccess) {
    availableFilters.push('lineManagerFilter');

    filterComponents.lineManagerFilter = (
      <LineManagerFilter
        autoFocus={autoFocus}
        division={currentDivision}
        filter={filters.lineManagerFilter}
        onDeselect={onDeselect}
        onFocus={onFocus}
        onRemoveFilter={onRemoveFilter}
        onSelect={onSelect}
        validationError={validationErrors.lineManagerFilter}
      />
    );
  }

  if (hasTeamViewableAccess && !hasLineManagerOnlyAccess) {
    availableFilters.push('teamFilter');
    filterComponents.teamFilter = (
      <TeamFilter
        autoFocus={autoFocus}
        filter={filters.teamFilter}
        isDivisionFilterSelected={selectedSet.has('divisionFilter')}
        onDeselect={onDeselect}
        onFocus={onFocus}
        onRemoveFilter={onRemoveFilter}
        onSelect={onSelect}
        validationError={validationErrors.teamFilter}
      />
    );
  }

  if (currentDivision.attributes.primary) {
    availableFilters.unshift('divisionFilter');

    filterComponents.divisionFilter = (
      <DivisionFilter
        autoFocus={autoFocus}
        filter={filters.divisionFilter}
        onDeselect={onDeselect}
        onFocus={onFocus}
        onRemoveFilter={onRemoveFilter}
        onSelect={onSelect}
        validationError={validationErrors.divisionFilter}
      />
    );
  }

  const areAllFiltersSelected = availableFilters.every((filter) =>
    selectedSet.has(filter),
  );

  useEffect(() => {
    if (isInitialMount.current) isInitialMount.current = false;
  }, []);

  return (
    <>
      <div className='tw-flex tw-items-center'>
        <label className='tw-m-0 tw-mr-2 tw-text-l tw-font-semibold tw-tracking-auto'>
          {i18nTShared('filters')}
        </label>
        <Tooltip
          placement='top'
          tooltip={
            <p className='tw-m-0'>
              <Trans
                components={{ span: <span className='tw-font-bold' /> }}
                i18nKey='trainingRegisterReports.components.Filters.tooltip'
                ns='components'
              />
            </p>
          }
          trigger='hover'
        >
          <CircleQuestion />
        </Tooltip>
      </div>
      <div className='tw-mt-6'>
        {filters.selectedFilters.map((filter) => (
          <div className='tw-mb-6' key={filter}>
            {filterComponents[filter]}
          </div>
        ))}
        <Options
          align='left'
          btnToggle={(isOpen) => (
            <div className='tooltip-parent'>
              <OutlinedButton
                color='grey'
                disabled={areAllFiltersSelected}
                onClick={() => {}}
                size='sm'
              >
                <span>{i18nTShared('addFilter')}</span>
                <ChevronUpIcon
                  className='-tw-mb-[1px] -tw-mr-[6px]'
                  height={24}
                  style={{ display: isOpen ? 'inline-block' : 'none' }}
                  width={24}
                />
                <ChevronDownIcon
                  className='-tw-mb-[1px] -tw-mr-[6px]'
                  height={24}
                  style={{ display: isOpen ? 'none' : 'inline-block' }}
                  width={24}
                />
              </OutlinedButton>
              {areAllFiltersSelected && (
                <Tooltip
                  placement='top'
                  tooltip={i18nTShared('allFiltersHaveBeenAdded')}
                  trigger='hover'
                />
              )}
            </div>
          )}
          buttonDisabled={areAllFiltersSelected}
          listStyle='tw-w-52 [&_li]:tw-border-none'
          toggleStyle='tw-inline-block'
        >
          {availableFilters.map(
            (filter) =>
              !selectedSet.has(filter) && (
                <OptionChoice
                  key={filter}
                  onClick={(setOptionsOpen) => {
                    setOptionsOpen(false);
                    onAddFilter(filter);
                  }}
                >
                  <a className='tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'>
                    {i18nTShared(filterOptionTKeys[filter])}
                  </a>
                </OptionChoice>
              ),
          )}
        </Options>
      </div>
    </>
  );
}

Filters.propTypes = {
  filters: PropTypes.object.isRequired,
  validationErrors: PropTypes.object,
  onAddFilter: PropTypes.func.isRequired,
  onRemoveFilter: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onDeselect: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
  defaultFilters: PropTypes.array.isRequired,
};
